/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import type { PageTypeData } from '@/types';
import {
  type ProductListingPageType,
  productListingPageTypes,
} from '../constants/productListingPageTypes';
import { getPathnameFromAsPath } from '@/utils/urlHelpers';
import {
  type AcesSearchSkuRecordResponse,
  type SearchSkuRecordResponse,
} from '../../search/interface';
import { type ParsedUrlQuery } from 'querystring';

export const generateContentStackPLPParams = ({
  router,
  isCSSearchResultsPageEnabled,
  pageTypeData,
  searchSkuRecordData,
  acesEnabled,
}: {
  router: {
    pathname: string;
    query: ParsedUrlQuery;
    asPath: string;
  };
  isCSSearchResultsPageEnabled: boolean;
  pageTypeData: PageTypeData | undefined;
  searchSkuRecordData: SearchSkuRecordResponse | AcesSearchSkuRecordResponse | undefined;
  acesEnabled: boolean;
}) => {
  let productListingPageType: ProductListingPageType | undefined;
  let taxonomyPath: string | undefined;
  let url: string | undefined;
  let isCSProductListingPageEnabled = false;
  const acesSearchSkuRecordData = searchSkuRecordData as AcesSearchSkuRecordResponse | undefined;
  const partsProSearchSkuRecordData = searchSkuRecordData as SearchSkuRecordResponse | undefined;
  switch (router.pathname) {
    case '/deals/[cmsPath]/[[...slug]]':
      productListingPageType = productListingPageTypes.merchShelf;
      taxonomyPath = `/deals/${router.query.cmsPath as string}`;
      url = `/deals/${router.query.cmsPath as string}`;
      isCSProductListingPageEnabled = true;
      break;
    case '/brands/[brandName]':
      productListingPageType = productListingPageTypes.brandShelfDefault;
      url = getPathnameFromAsPath(router.asPath);
      isCSProductListingPageEnabled = true;
      break;
    case '/[...seoUrlPath]':
      taxonomyPath = pageTypeData?.botEnabledFacetPath
        ? `${pageTypeData.taxonomyPath}${pageTypeData.botEnabledFacetPath}`
        : (pageTypeData?.taxonomyPath ?? '');
      productListingPageType = productListingPageTypes.default;
      isCSProductListingPageEnabled = pageTypeData?.pageType === 'ProductShelf';
      break;
    case '/searchresult':
      url = acesEnabled
        ? acesSearchSkuRecordData?.searchResults?.skuRecord?.taxonomyPath
        : partsProSearchSkuRecordData?.skuRecord?.taxonomyPath;
      productListingPageType = productListingPageTypes.searchResult;
      isCSProductListingPageEnabled = !!url && isCSSearchResultsPageEnabled;
      break;
  }

  return { productListingPageType, taxonomyPath, url, isCSProductListingPageEnabled };
};
